/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { shallow } from 'zustand/shallow';
import { createSelector } from 'zustand/traditional';
import type { ProductStore } from '../productStore';
import type { GameStore } from '../gameStore';
import type { CompanyStore } from '../companyStore';
import type { MarketStore } from '../marketStore';
import type { InventoryStore } from '../inventoryStore';
import type { ProductionStore } from '../productionStore';
import type { UIStore } from '../uiStore';
import type { Product } from '../../types/product';

// ============================================
// PRODUCT STORE - Fine-grained selectors
// ============================================

// Single value selectors - minimal re-renders
export const useProductById = (id: string) => (state: ProductStore) => 
  state.products.find(p => p.id === id);

export const useProductName = (id: string) => (state: ProductStore) => 
  state.products.find(p => p.id === id)?.name;

export const useProductPrice = (id: string) => (state: ProductStore) => 
  state.products.find(p => p.id === id)?.price;

export const useProductStatus = (id: string) => (state: ProductStore) => 
  state.products.find(p => p.id === id)?.status;

export const useProductRevenue = (id: string) => (state: ProductStore) => 
  state.products.find(p => p.id === id)?.salesMetrics.revenue;

export const useActiveProductId = () => (state: ProductStore) => 
  state.activeProduct?.id;

// Array selectors with shallow comparison
export const useProductIds = () => (state: ProductStore) => 
  state.products.map(p => p.id);

export const useProductsByStatus = (status: Product['status']) => (state: ProductStore) => 
  state.products.filter(p => p.status === status).map(p => p.id);

export const useProductsInDevelopment = () => (state: ProductStore) => 
  state.products.filter(p => 
    ['development', 'design', 'engineering', 'testing'].includes(p.status)
  ).map(p => p.id);

// Computed selectors with memoization
export const useProductMetrics = (id: string) => (state: ProductStore) => {
  const product = state.products.find(p => p.id === id);
  if (!product) return null;
  
  return {
    revenue: product.salesMetrics.revenue,
    unitsSold: product.salesMetrics.unitsSold,
    marketShare: product.salesMetrics.marketShare,
    profit: product.salesMetrics.revenue - (product.productionCost * product.salesMetrics.unitsSold),
    margin: ((product.price - product.productionCost) / product.price) * 100,
  };
};

export const useProductsByPhase = () => (state: ProductStore) => {
  const phases = new Map<Product['status'], string[]>();
  state.products.forEach(product => {
    const phase = product.status;
    if (!phases.has(phase)) phases.set(phase, []);
    phases.get(phase)!.push(product.id);
  });
  return Object.fromEntries(phases);
};

// Pipeline selectors
export const usePipelineCount = (status: Product['status']) => (state: ProductStore) => 
  state.pipeline[status]?.length || 0;

export const useTotalProductCount = () => (state: ProductStore) => 
  state.products.length;

// ============================================
// GAME STORE - Temporal selectors
// ============================================

export const useGameDate = () => (state: GameStore) => state.gameDate;
export const useGameDay = () => (state: GameStore) => state.gameDate.day;
export const useGameMonth = () => (state: GameStore) => state.gameDate.month;
export const useGameYear = () => (state: GameStore) => state.gameDate.year;
export const useGameSpeed = () => (state: GameStore) => state.simulationSpeed;
export const useIsPaused = () => (state: GameStore) => state.isPaused;

export const useFormattedGameDate = () => (state: GameStore) => {
  const { day, month, year } = state.gameDate;
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${monthNames[month - 1]} ${day}, ${year}`;
};

// ============================================
// COMPANY STORE - Business metrics
// ============================================

export const useCompanyName = () => (state: CompanyStore) => state.company.name;
export const useCompanyFunds = () => (state: CompanyStore) => state.company.funds;
export const useCompanyRevenue = () => (state: CompanyStore) => state.finances.revenue;
export const useCompanyExpenses = () => (state: CompanyStore) => state.finances.expenses;
export const useCompanyProfit = () => (state: CompanyStore) => 
  state.finances.revenue - state.finances.expenses;

export const useResearchPoints = () => (state: CompanyStore) => 
  state.research?.researchPoints || 0;

export const useCompanyMetrics = () => (state: CompanyStore) => ({
  funds: state.company.funds,
  revenue: state.finances.revenue,
  expenses: state.finances.expenses,
  profit: state.finances.revenue - state.finances.expenses,
  researchPoints: state.research?.researchPoints || 0,
});

// Research selectors
export const useUnlockedResearch = () => (state: CompanyStore) => 
  state.research.unlockedNodes.map(nodeId => nodeId);

export const useIsResearchUnlocked = (nodeId: string) => (state: CompanyStore) => 
  state.research.unlockedNodes.includes(nodeId);

export const useActiveResearch = () => (state: CompanyStore) => 
  state.research.activeResearch;

export const useResearchProgress = (nodeId: string) => (state: CompanyStore) => 
  state.research.activeResearch?.nodeId === nodeId 
    ? state.research.activeResearch.progress 
    : 0;

// Consolidated research selector for better performance
export const useResearchData = () => (state: CompanyStore) => ({
  researchPoints: state.research?.researchPoints || 0,
  unlockedNodes: state.research?.unlockedNodes || [],
  activeResearch: state.research?.activeResearch || [],
  monthlyInvestment: state.research?.monthlyInvestment || 0,
  rpGenerationRate: state.research?.rpGenerationRate || 1,
});

// ============================================
// MARKET STORE - Competition & market data
// ============================================

export const useMarketShare = () => (state: MarketStore) => 
  state.marketShare.playerShare;

export const useCompetitorCount = () => (state: MarketStore) => 
  state.competitors.length;

export const useActiveMarketEvents = () => (state: MarketStore) => 
  state.marketEvents.filter(event => event.active).map(e => e.id);

export const useEconomicPhase = () => (state: MarketStore) => 
  state.economicCycle.currentPhase;

export const useMarketDemand = () => (state: MarketStore) => 
  state.economicCycle.metrics.consumerConfidence;

export const useCompetitorActions = () => (state: MarketStore) => 
  state.competitorActions.slice(0, 5).map(a => a.id); // Last 5 actions

// ============================================
// INVENTORY STORE - Supply chain
// ============================================

export const useComponentStock = (componentId: string) => (state: InventoryStore) => 
  state.components.find(c => c.id === componentId)?.quantity || 0;

export const useComponentCost = (componentId: string) => (state: InventoryStore) => 
  state.costs[componentId] || 0;

export const useLowStockComponents = () => (state: InventoryStore) => 
  state.components
    .filter(c => c.quantity < c.reorderPoint)
    .map(c => c.id);

export const usePendingOrders = () => (state: InventoryStore) => 
  state.purchaseOrders
    .filter(order => order.status === 'pending')
    .map(o => o.id);

export const useSupplyChainHealth = () => (state: InventoryStore) => {
  const totalComponents = state.components.length;
  const lowStock = state.components.filter(c => c.quantity < c.reorderPoint).length;
  return totalComponents > 0 ? ((totalComponents - lowStock) / totalComponents) * 100 : 100;
};

// ============================================
// PRODUCTION STORE - Manufacturing
// ============================================

export const useActiveBatches = () => (state: ProductionStore) => 
  state.batches.filter(b => b.status === 'in_production').map(b => b.id);

export const useProductionCapacity = () => (state: ProductionStore) => ({
  used: state.capacity.used,
  total: state.capacity.total,
  available: state.capacity.total - state.capacity.used,
  utilization: (state.capacity.used / state.capacity.total) * 100,
});

export const useBatchProgress = (batchId: string) => (state: ProductionStore) => 
  state.batches.find(b => b.id === batchId)?.progress || 0;

export const useQualityMetrics = () => (state: ProductionStore) => ({
  defectRate: state.qualityMetrics.defectRate,
  yieldRate: state.qualityMetrics.yieldRate,
  qualityScore: state.qualityMetrics.qualityScore,
});

// ============================================
// UI STORE - Interface state
// ============================================

export const useActiveModal = () => (state: UIStore) => state.activeModal;
export const useSelectedTab = () => (state: UIStore) => state.selectedTab;
export const useNotifications = () => (state: UIStore) => 
  state.notifications.map(n => n.id);

export const useIsModalOpen = (modalName: string) => (state: UIStore) => 
  state.activeModal === modalName;

export const useDebugPanelState = (panelName: string) => (state: UIStore) => 
  state.debugPanels[panelName] || false;

// ============================================
// MULTI-STORE SELECTORS - Complex computations
// ============================================

export const useProductionReadiness = (productId: string) => {
  const product = useProductById(productId);
  const companyFunds = useCompanyFunds();
  const inventoryHealth = useSupplyChainHealth();
  const productionCapacity = useProductionCapacity();
  
  return (state: { product: ProductStore; company: CompanyStore; inventory: InventoryStore; production: ProductionStore }) => {
    const prod = state.product.products.find(p => p.id === productId);
    if (!prod) return { ready: false, reasons: ['Product not found'] };
    
    const reasons: string[] = [];
    
    if (prod.status !== 'manufacturing') {
      reasons.push('Product not in manufacturing stage');
    }
    
    if (state.company.company.funds < prod.productionCost * 100) {
      reasons.push('Insufficient funds for production');
    }
    
    if (state.production.capacity.available < 1) {
      reasons.push('No production capacity available');
    }
    
    const lowStock = state.inventory.components.filter(c => c.quantity < c.reorderPoint);
    if (lowStock.length > 0) {
      reasons.push('Low component inventory');
    }
    
    return {
      ready: reasons.length === 0,
      reasons,
    };
  };
};

// ============================================
// PERFORMANCE HELPERS
// ============================================

// Shallow equality check for arrays
export const useShallowArray = <T>(selector: (state: T) => any[]) => 
  (state: T) => selector(state);

// Memoized expensive computations
export const createMemoizedSelector = <TState, TResult>(
  selector: (state: TState) => TResult,
  equalityFn = shallow
) => {
  let lastState: TState;
  let lastResult: TResult;
  
  return (state: TState): TResult => {
    if (lastState && equalityFn(state, lastState)) {
      return lastResult;
    }
    
    lastState = state;
    lastResult = selector(state);
    return lastResult;
  };
};

// ============================================
// HOOKS FOR DIRECT STORE ACCESS
// ============================================

import { useProductStore } from '../productStore';
import { useGameStore } from '../gameStore';
import { useCompanyStore } from '../companyStore';
import { useMarketStore } from '../marketStore';
import { useInventoryStore } from '../inventoryStore';
import { useProductionStore } from '../productionStore';
import { useUIStore } from '../uiStore';

// Product hooks
export const useProduct = (id: string) => useProductStore(useProductById(id));
export const useProductField = <K extends keyof Product>(id: string, field: K) => 
  useProductStore(state => state.products.find(p => p.id === id)?.[field]);

// Actions with stable references
export const useProductActions = () => {
  const createProduct = useProductStore(state => state.createProduct);
  const updateProduct = useProductStore(state => state.updateProduct);
  const deleteProduct = useProductStore(state => state.deleteProduct);
  const setActiveProduct = useProductStore(state => state.setActiveProduct);
  
  return { createProduct, updateProduct, deleteProduct, setActiveProduct };
};

export const useGameActions = () => {
  const pauseGame = useGameStore(state => state.pauseGame);
  const resumeGame = useGameStore(state => state.resumeGame);
  const setSimulationSpeed = useGameStore(state => state.setSimulationSpeed);
  const updateGameDate = useGameStore(state => state.updateGameDate);
  
  return { pauseGame, resumeGame, setSimulationSpeed, updateGameDate };
};

export const useCompanyActions = () => {
  const updateCompany = useCompanyStore(state => state.updateCompany);
  const updateFinances = useCompanyStore(state => state.updateFinances);
  const startResearch = useCompanyStore(state => state.startResearch);
  const completeResearch = useCompanyStore(state => state.completeResearch);
  
  return { updateCompany, updateFinances, startResearch, completeResearch };
};